import { useEffect, useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { WsHubMessage } from '@constants/ws-hub';
import { alertActions } from '@actions';
import { hubService, exportService } from '@services';
import { useTranslation } from '@hooks/useTranslation';

const POLL_INTERVAL = 5000;
const POLL_TTL = 10 * 60 * 1000; // 10 minutes
const pendingExports = new Map(); // exportStatusId -> trackedAt timestamp

export const trackExport = exportStatusId => {
    pendingExports.set(exportStatusId, Date.now());
};

export const clearPendingExports = () => {
    pendingExports.clear();
};

export const useExportNotifications = () => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const tRef = useRef(t);
    tRef.current = t;

    const pollTimerRef = useRef(null);

    const updateExportToast = useCallback(
        (exportStatusId, status, fileUrl) => {
            pendingExports.delete(exportStatusId);
            dispatch(alertActions.clear(exportStatusId));

            if (status === 'completed') {
                dispatch(
                    alertActions.success(tRef.current('exports.ready'), {
                        id: exportStatusId,
                        persistent: true,
                        actionUrl: fileUrl,
                        actionLabel: tRef.current('buttons.download'),
                    }),
                );
            } else if (status === 'failed') {
                dispatch(alertActions.error(tRef.current('exports.failed'), { id: exportStatusId }));
            }
        },
        [dispatch],
    );

    const handleExportStatus = useCallback(
        data => {
            updateExportToast(data.exportStatusId, data.status, data.fileUrl);
        },
        [updateExportToast],
    );

    const pollPendingExports = useCallback(async () => {
        const now = Date.now();
        for (const [exportStatusId, trackedAt] of [...pendingExports]) {
            if (now - trackedAt > POLL_TTL) {
                pendingExports.delete(exportStatusId);
                dispatch(alertActions.clear(exportStatusId));
                continue;
            }
            try {
                const data = await exportService.getStatus(exportStatusId);
                if (!pendingExports.has(exportStatusId)) continue;
                if (data.status === 'completed' || data.status === 'failed') {
                    updateExportToast(data.exportStatusId, data.status, data.fileUrl);
                }
            } catch {
                // Transient error (network, 500, 502) — retry on next interval
            }
        }
    }, [dispatch, updateExportToast]);

    useEffect(() => {
        pollTimerRef.current = setInterval(() => {
            if (pendingExports.size > 0) {
                pollPendingExports();
            }
        }, POLL_INTERVAL);

        return () => clearInterval(pollTimerRef.current);
    }, [pollPendingExports]);

    useEffect(() => {
        hubService.subscribeToEvents({
            [WsHubMessage.ExportStatus]: handleExportStatus,
        });

        return () => {
            hubService.unsubscribeFromEvents([WsHubMessage.ExportStatus]);
        };
    }, [handleExportStatus]);
};
